import { defineStore } from 'pinia'

interface ANCAPRating {
  id: string
  make: string
  model: string
  variant?: string
  yearFrom: number
  yearTo?: number
  starRating: number
  testYear: number
  collisionAvoidance?: 'Platinum' | 'Gold' | 'Silver' | 'Bronze'
  safetyScore?: number
  adultOccupant?: number
  childOccupant?: number
  vulnerableRoadUsers?: number
  safetyAssist?: number
  technicalReportUrl?: string
  energySources?: string
  isCurrentModel?: boolean
  autonomousEmergencyBraking?: string
  laneSupportSystem?: string
}

interface ANCAPCache {
  [key: string]: {
    data: ANCAPRating | null
    timestamp: number
    expires: number
  }
}

interface ANCAPState {
  cache: ANCAPCache
  loading: { [key: string]: boolean }
  errors: { [key: string]: string | null }
}

export const useANCAPStore = defineStore('ancap', {
  state: (): ANCAPState => ({
    cache: {},
    loading: {},
    errors: {},
  }),

  getters: {
    getRating: (state) => (make: string, model: string, year: number): ANCAPRating | null => {
      const key = `${make}-${model}-${year}`.toLowerCase()
      const cached = state.cache[key]
      
      if (!cached) return null
      
      // Check if cache is still valid (24 hours)
      if (Date.now() > cached.expires) {
        return null
      }
      
      return cached.data
    },

    isLoading: (state) => (make: string, model: string, year: number): boolean => {
      const key = `${make}-${model}-${year}`.toLowerCase()
      return state.loading[key] || false
    },

    getError: (state) => (make: string, model: string, year: number): string | null => {
      const key = `${make}-${model}-${year}`.toLowerCase()
      return state.errors[key] || null
    }
  },

  actions: {
    _setCacheInternal(make: string, model: string, year: number, data: ANCAPRating | null, error: string | null = null) {
      const key = `${make}-${model}-${year}`.toLowerCase()
      const now = Date.now()
      
      this.cache[key] = {
        data,
        timestamp: now,
        expires: now + (24 * 60 * 60 * 1000) // 24 hours
      }
      
      this.loading[key] = false
      this.errors[key] = error
    },

    async fetchRating(make: string, model: string, year: number, force: boolean = false): Promise<ANCAPRating | null> {
      const key = `${make}-${model}-${year}`.toLowerCase()
      
      // Check if we already have valid cached data and not forcing
      if (!force) {
        const cached = this.getRating(make, model, year)
        if (cached) {
          return cached
        }
      }

      // Check if already loading
      if (this.loading[key]) {
        // Wait for current request to complete
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!this.loading[key]) {
              clearInterval(checkInterval)
              resolve(this.getRating(make, model, year))
            }
          }, 100)
        })
      }

      this.loading[key] = true
      this.errors[key] = null

      try {
        let response: any

        if (process.server) {
          // Server-side fetch using h3 utilities
          const { ancapApiService } = await import('../server/services/ancap-api')
          const ancapRating = await ancapApiService.findRating(make, model, year)
          
          if (ancapRating) {
            this._setCacheInternal(make, model, year, ancapRating)
            return ancapRating
          } else {
            this._setCacheInternal(make, model, year, null, 'No ANCAP rating found for this vehicle')
            return null
          }
        } else {
          // Client-side fetch
          response = await $fetch(`/api/ancap-rating`, {
            query: { make, model, year }
          })

          if (response.success && response.data) {
            this._setCacheInternal(make, model, year, response.data)
            return response.data
          } else {
            this._setCacheInternal(make, model, year, null, response.error || 'No ANCAP rating found')
            return null
          }
        }
      } catch (error: any) {
        console.error('[ANCAP Store] Error fetching rating:', error)
        const errorMessage = error.message || 'Failed to fetch ANCAP rating'
        this._setCacheInternal(make, model, year, null, errorMessage)
        return null
      }
    },

    async ensureRating(make: string, model: string, year: number): Promise<ANCAPRating | null> {
      // This method ensures we have the rating data, fetching if necessary
      // but doesn't force refresh if we have valid cached data
      return await this.fetchRating(make, model, year, false)
    },

    clearCache() {
      this.cache = {}
      this.loading = {}
      this.errors = {}
    },

    clearExpiredCache() {
      const now = Date.now()
      Object.keys(this.cache).forEach(key => {
        if (this.cache[key].expires <= now) {
          delete this.cache[key]
          delete this.loading[key]
          delete this.errors[key]
        }
      })
    }
  }
})